import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { computed } from 'vue';
import type { OutPriceConfigDto, OutPriceDto } from '@/ApiClient.ts';
import { containsKeyReference } from '@/components/prices/utils/liquid-calculation';
import { useDestinations } from '@/store/destinations';
import { useSelectedDestination } from '@/store/selected-destination';

export const useOutPriceConfigs = defineStore('outPriceConfigs', () => {
  const api = useApi();

  const globalStoreBase = useGlobalStore({
    load: api.outPriceConfig_GetAll,
    dto: 'OutPriceConfigDto',
    update: api.outPriceConfig_UpdateOutPriceConfig,
    signalR: 'OutPriceConfigUpdate',
  });

  const { loaded } = globalStoreBase;

  /** The DEFAULT DESTINATION's seasonless config. */
  const defaultConfig = computed(() => defaultConfigFor(useDestinations().defaultDestination?.id));

  const defaultConfigFor = (destinationId?: number) =>
    loaded.value.find((config) => config.seasonId == null && config.destinationId === destinationId);

  const seasonConfigs = computed(() => loaded.value.filter((config) => config.seasonId != null));

  /**
   * Key identifies an out-price across seasons, where its row id does not. At most one match:
   * keys are unique per brand+season.
   */
  const seasonlessOutPriceWithKey = (key: string): OutPriceDto | undefined =>
    loaded.value
      .filter((config) => config.seasonId == null)
      .flatMap((config) => config.outPrices)
      .find((outPrice) => outPrice.key === key);

  /**
   * Config for (season, destination), falling back to the destination's seasonless default —
   * never across destinations. Undefined destination resolves to the default destination.
   */
  const getBySeasonAndDestination = (seasonId?: number | null, destinationId?: number) => {
    const resolvedDestinationId = destinationId ?? useDestinations().defaultDestination?.id;
    return (
      loaded.value.find(
        (config) => (config.seasonId ?? null) === (seasonId ?? null) && config.destinationId === resolvedDestinationId
      ) ?? defaultConfigFor(resolvedDestinationId)
    );
  };

  /** Selected-destination resolution (the market the user is working in) — for read-only summaries/teasers. */
  const getBySeasonId = (seasonId?: number | null) =>
    getBySeasonAndDestination(seasonId, useSelectedDestination().selectedDestination?.id);

  /**
   * For the given season: every destination's resolved config's out-prices, default destination
   * first (drives the overview's all-destination columns and filters).
   */
  const outPricesForSeason = (seasonId?: number | null): { outPrice: OutPriceDto; config: OutPriceConfigDto }[] => {
    const result: { outPrice: OutPriceDto; config: OutPriceConfigDto }[] = [];
    for (const destination of useDestinations().loaded) {
      const config = getBySeasonAndDestination(seasonId, destination.id);
      if (!config) continue;
      for (const outPrice of config.outPrices) result.push({ outPrice, config });
    }
    return result;
  };

  const getByOutPriceId = (outPriceId: number) => {
    const config = loaded.value.find((config) => config.outPrices.some((op) => op.id === outPriceId));
    if (!config) {
      throw new Error(`OutPriceConfig not found for OutPriceId ${outPriceId}`);
    }

    return config;
  };

  const outPriceById = computed(() => {
    const map: Record<number, OutPriceDto> = {};
    loaded.value.forEach((config) => {
      config.outPrices.forEach((outPrice) => {
        map[outPrice.id] = outPrice;
      });
    });

    return map;
  });

  const getDependentOutPrices = (outPriceId: number): OutPriceDto[] => {
    const outPrice = outPriceById.value[outPriceId];
    const config = getByOutPriceId(outPriceId);
    return config.outPrices.filter(
      (op) => op.calculationTemplate && containsKeyReference(op.calculationTemplate, outPrice.key)
    );
  };

  // Get the main out price (first in config, lowest _Order) for a season + destination (default destination when omitted)
  const getMainOutPrice = (seasonId?: number | null, destinationId?: number) => {
    return getBySeasonAndDestination(seasonId, destinationId)?.outPrices[0];
  };

  // Get the main out price value from a list of out price values
  const getMainOutPriceValue = <T extends { outPriceId: number }>(
    outPriceValues: T[],
    seasonId?: number | null,
    destinationId?: number
  ): T | undefined => {
    const mainOutPriceId = getMainOutPrice(seasonId, destinationId)?.id;
    if (!mainOutPriceId) return undefined;
    return outPriceValues.find((opv) => opv.outPriceId === mainOutPriceId);
  };

  return {
    ...globalStoreBase,
    defaultConfig,
    defaultConfigFor,
    seasonConfigs,
    seasonlessOutPriceWithKey,
    getBySeasonId,
    getBySeasonAndDestination,
    outPricesForSeason,
    getByOutPriceId,
    outPriceById,
    getDependentOutPrices,
    getMainOutPrice,
    getMainOutPriceValue,
  };
});
